import { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Scale, Loader2 } from "lucide-react";
import { reconcileSlitBatch } from "@/lib/api";
import { fmtKg } from "@/lib/utils";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";

const ReconcileDialog = ({ open, onOpenChange, batch, onDone }) => {
  const [scrap, setScrap] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const qc = useQueryClient();

  if (!batch) return null;
  const motherWeight = Number(batch.mother_weight_kg) || 0;
  const theoreticalYield = Number(batch.theoretical_yield_pct) || 0;
  const theoreticalRecovery = motherWeight && batch.total_slit_weight_kg ? Number(batch.total_slit_weight_kg) : 0;
  const scrapNum = Number(scrap);
  const previewYield = motherWeight && scrap !== "" ? Math.max(0, ((motherWeight - scrapNum) / motherWeight) * 100).toFixed(2) : null;
  const overWeight = scrapNum > motherWeight;
  const canSubmit = !saving && scrap !== "" && scrapNum >= 0 && !overWeight;

  const handleSave = async () => {
    if (!canSubmit) return;
    setSaving(true);
    try {
      const res = await reconcileSlitBatch(batch.id, {
        scrap_kg: Number(scrap),
        notes: notes.trim(),
      });
      toast.success(`Yield recorded: ${res.actual_yield_pct}%`);
      qc.invalidateQueries({ queryKey: ["batches"] });
      qc.invalidateQueries({ queryKey: ["coil-tree"] });
      onDone?.(res);
      onOpenChange(false);
      setScrap(""); setNotes("");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reconcile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reconcile-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Scale className="h-5 w-5" /> Reconcile slitting yield
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="border border-slate-100 bg-slate-50 px-3 py-2 rounded-sm">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">RM</p>
              <p className="font-mono-data text-sm text-slate-900 mt-0.5 truncate">{batch.mother_coil_id}</p>
            </div>
            <div className="border border-slate-100 bg-slate-50 px-3 py-2 rounded-sm">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">RM Wt</p>
              <p className="font-mono-data text-sm text-slate-900 mt-0.5">{motherWeight.toLocaleString()} kg</p>
            </div>
            <div className="border border-slate-100 bg-slate-50 px-3 py-2 rounded-sm">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">SFG Wt</p>
              <p className="font-mono-data text-sm text-slate-900 mt-0.5">{theoreticalRecovery.toLocaleString()} kg</p>
            </div>
            <div className="border border-slate-100 bg-slate-50 px-3 py-2 rounded-sm">
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 font-semibold">Theoretical Yield</p>
              <p className="font-mono-data text-sm text-slate-900 mt-0.5">{theoreticalYield}%</p>
            </div>
          </div>

          <div>
            <Label className="text-xs font-semibold tracking-wide uppercase text-slate-600">Scrap weight (kg) *</Label>
            <Input
              data-testid="reconcile-scrap-input"
              type="number"
              step="0.01"
              min="0"
              value={scrap}
              onChange={(e) => setScrap(e.target.value)}
              placeholder="Weigh the scrap pile and enter total"
              autoFocus
              className="font-mono-data"
            />
            {previewYield !== null && !overWeight && (
              <p data-testid="reconcile-yield-preview" className="text-xs text-slate-600 mt-1.5">
                Actual yield will be <span className="font-mono-data font-bold text-slate-900">{previewYield}%</span> (vs theoretical {theoreticalYield}%)
                {Number(previewYield) < theoreticalYield - 1 && (
                  <span className="ml-2 text-amber-700 font-semibold">⚠ {(theoreticalYield - Number(previewYield)).toFixed(2)}% below theoretical</span>
                )}
              </p>
            )}
            {overWeight && (
              <p className="text-xs text-red-700 mt-1.5">Scrap cannot exceed mother weight ({fmtKg(motherWeight)} kg)</p>
            )}
          </div>

          <div>
            <Label className="text-xs font-semibold tracking-wide uppercase text-slate-600">Notes (optional)</Label>
            <Textarea
              data-testid="reconcile-notes-input"
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Blade condition, machine settings, anomalies…"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reconcile-cancel-btn" className="rounded-sm">
            Later
          </Button>
          <Button onClick={handleSave} disabled={!canSubmit} data-testid="reconcile-save-btn" className="bg-slate-900 hover:bg-slate-800 text-white rounded-sm">
            {saving ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving…</> : "Save reconciliation"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReconcileDialog;
